import React, { useState } from 'react';
import {
  Box, Typography, Stack, Card, Avatar, Chip, IconButton,
  BottomNavigation, BottomNavigationAction, Button, Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Home as HomeIcon,
  Timeline as ProgressIcon,
  GroupAdd as JoinIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import PhoneFrame, { AutomatrixBanner, NavBar } from './PhoneFrame';
import {
  APP_COLORS, IMPACT, TIMELINE_EVENTS, ACHIEVEMENTS as SHARED_ACHIEVEMENTS,
  CLASS_MEMBERSHIPS,
} from '../sharedMockData';

/**
 * Student progress tab. Timeline, impact points, class roster, achievements.
 * Includes an empty-state toggle for review.
 */

const INITIALS = { icivics: 'iC', khan: 'KA', readtheory: 'RT', duolingo: 'DL', typingclub: 'TC' };

const TIMELINE = TIMELINE_EVENTS.map(e => ({
  app: e.app,
  title: e.title,
  when: `${e.day} · ${e.when}`,
  icon: INITIALS[e.appKey] || e.app.slice(0, 2).toUpperCase(),
  color: APP_COLORS[e.appKey] || '#546e7a',
  points: e.points,
}));

const ACHIEVEMENTS = SHARED_ACHIEVEMENTS.slice(0, 3).map(a => ({ label: a.label, icon: a.emoji }));

const CLASSES = CLASS_MEMBERSHIPS.map(c => ({
  name: c.name,
  teacher: `${c.teacher} · ${c.app}`,
  members: c.appKey === 'icivics' ? 24 : c.appKey === 'khan' ? 31 : 18,
}));

const AndroidProgressMockup = () => {
  const [empty, setEmpty] = useState(false);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 2 }}>
      <PhoneFrame background="#f6f7fb">
        <AutomatrixBanner />

        {/* App bar */}
        <Box sx={{
          px: 2, pt: 1, pb: 1.25, bgcolor: '#fff',
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: '1px solid #ececf2',
        }}>
          <IconButton size="small"><BackIcon sx={{ fontSize: 20 }} /></IconButton>
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
            Progress
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            onClick={() => setEmpty(e => !e)}
            sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#8a8d93' }}
          >
            {empty ? 'Show data' : 'Empty state'}
          </Button>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {/* Impact points header */}
          <Card
            elevation={0}
            sx={{
              m: 2, p: 2, borderRadius: 4,
              background: 'linear-gradient(135deg, #ff6b35 0%, #f47738 100%)',
              color: '#fff',
              boxShadow: '0 6px 14px rgba(255,107,53,0.22)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box sx={{
                width: 42, height: 42, borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.22)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <TrendingIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="caption" sx={{ color: '#ffe3d4', fontSize: '0.65rem' }}>
                  IMPACT POINTS
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1 }}>
                  {empty ? 0 : IMPACT.total.toLocaleString()}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={empty ? '—' : `+${IMPACT.weekDelta} this week`}
                sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700, fontSize: '0.65rem' }}
              />
            </Stack>
          </Card>

          {empty ? (
            <Box sx={{
              mx: 2, p: 3, textAlign: 'center',
              bgcolor: '#fff', borderRadius: 4, border: '1px dashed #d7d9e0',
            }}>
              <SchoolIcon sx={{ fontSize: 40, color: '#c3c6cf', mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                No activities yet
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b6f76', fontSize: '0.72rem', display: 'block', mt: 0.5, mb: 2 }}>
                Start a course from your dashboard to see your progress here.
              </Typography>
              <Button
                variant="contained"
                sx={{ borderRadius: 20, textTransform: 'none', fontWeight: 700, bgcolor: '#1976d2' }}
              >
                Browse apps
              </Button>
            </Box>
          ) : (
            <>
              {/* Achievements row */}
              <Box sx={{ px: 2, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.82rem' }}>
                  Recent achievements
                </Typography>
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                  {ACHIEVEMENTS.map(a => (
                    <Card
                      key={a.label}
                      elevation={0}
                      sx={{
                        minWidth: 100, p: 1.25, borderRadius: 3,
                        border: '1px solid #e6e8ef', bgcolor: '#fff', textAlign: 'center',
                      }}
                    >
                      <Box sx={{ fontSize: '1.4rem', mb: 0.25 }}>{a.icon}</Box>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
                        {a.label}
                      </Typography>
                    </Card>
                  ))}
                </Stack>
              </Box>

              {/* Timeline */}
              <Box sx={{ px: 2, mt: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.82rem' }}>
                  Activity timeline
                </Typography>
                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e6e8ef', bgcolor: '#fff' }}>
                  {TIMELINE.map((e, idx) => (
                    <React.Fragment key={idx}>
                      <Box sx={{ p: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Avatar
                          variant="rounded"
                          sx={{ width: 32, height: 32, bgcolor: e.color, fontSize: '0.7rem', fontWeight: 700 }}
                        >
                          {e.icon}
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.25 }} noWrap>
                            {e.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                            <Typography sx={{ fontSize: '0.65rem', color: '#6b6f76' }}>
                              {e.app}
                            </Typography>
                            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#c3c6cf' }} />
                            <Typography sx={{ fontSize: '0.65rem', color: '#8a8d93' }}>
                              {e.when}
                            </Typography>
                          </Stack>
                        </Box>
                        {e.points > 0 && (
                          <Chip
                            size="small"
                            label={`+${e.points}`}
                            icon={<CheckIcon sx={{ fontSize: 12 }} />}
                            sx={{ height: 22, fontSize: '0.65rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }}
                          />
                        )}
                      </Box>
                      {idx < TIMELINE.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Card>
              </Box>

              {/* Class roster */}
              <Box sx={{ px: 2, mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.82rem' }}>
                  Your classes
                </Typography>
                <Stack spacing={1}>
                  {CLASSES.map(c => (
                    <Card
                      key={c.name}
                      elevation={0}
                      sx={{ p: 1.5, borderRadius: 3, border: '1px solid #e6e8ef', bgcolor: '#fff' }}
                    >
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                        {c.name}
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.25 }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#6b6f76' }}>
                          {c.teacher}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${c.members} students`}
                          sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#eef2ff', color: '#3949ab', fontWeight: 600 }}
                        />
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Box>

        <BottomNavigation
          showLabels
          value={1}
          sx={{
            borderTop: '1px solid #ececf2',
            bgcolor: '#fff',
            height: 56,
            '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
            '& .MuiBottomNavigationAction-label': { fontSize: '0.62rem', fontWeight: 600 },
          }}
        >
          <BottomNavigationAction label="Dashboard" icon={<HomeIcon />} />
          <BottomNavigationAction label="Progress" icon={<ProgressIcon />} />
          <BottomNavigationAction label="Join Class" icon={<JoinIcon />} />
          <BottomNavigationAction label="Profile" icon={<PersonIcon />} />
        </BottomNavigation>

        <NavBar />
      </PhoneFrame>
    </Box>
  );
};

export default AndroidProgressMockup;
